import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ChevronLeft, Store, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import CakeCustomizer from '../components/CakeCustomizer';
import { getImageUrl } from '../utils/imageUrl';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#8D6E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-[#1a1a1a]">Product not found</h1>
        <Link to="/shop">
          <Button className="mt-4 rounded-full">Back to Shop</Button>
        </Link>
      </div>
    );
  }

  // Gallery images - use product images array or just the main image
  const galleryImages = product.images && product.images.length > 0 
    ? product.images.filter(img => img).map(img => getImageUrl(img))
    : [getImageUrl(product.image)].filter(img => img);

  const primaryImage = galleryImages[0] || "";
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": primaryImage,
    "brand": {"@type": "Brand", "name": "Mad Brothers Bakery"},
    "category": product.category || "Bakery",
    "offers": {
      "@type": "Offer",
      "price": product.base_price,
      "priceCurrency": "EUR",
      "availability": product.in_stock === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      "url": typeof window !== "undefined" ? window.location.href : "",
      "seller": {"@type": "Bakery", "name": "Mad Brothers Bakery"}
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="product-page">
      <SEO
        title={`${product.name} - Order Custom in Dublin`}
        description={`${product.description || product.name} from Mad Brothers Bakery, Dublin 7. Handcrafted celebration cake available for collection. From €${product.base_price?.toFixed(2)}.`}
        keywords={`${product.name} Dublin, ${product.name} Ireland, custom ${product.name}, order ${product.name} online, ${product.category || 'cake'} Dublin`}
        image={primaryImage}
        type="product"
        jsonLd={productJsonLd}
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/shop" 
            className="inline-flex items-center text-stone-600 hover:text-[#1a1a1a] transition-colors"
            data-testid="back-to-shop"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Shop
          </Link>
        </div>
      </div>

      {/* Product Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            {/* Main Image */}
            <div className="aspect-square rounded-3xl overflow-hidden bg-white">
              <img
                src={galleryImages[selectedImage] || getImageUrl(product.image)}
                alt={product.name}
                loading="eager"
                fetchpriority="high"
                decoding="async"
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </div>

            {/* Thumbnails - only show if more than 1 image */}
            {galleryImages.length > 1 && (
              <div className="flex gap-3 flex-wrap">
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      selectedImage === idx 
                        ? 'border-[#8D6E63] shadow-md' 
                        : 'border-transparent opacity-70 hover:opacity-100'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} view ${idx + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Product Details & Customizer */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:sticky lg:top-32 self-start"
          >
            <CakeCustomizer product={product} />

            {/* Trust Badges */}
            <div className="mt-8 pt-8 border-t border-stone-200">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Store className="w-5 h-5 text-[#8D6E63]" />
                  </div>
                  <p className="text-xs text-stone-600">Free Collection</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Clock className="w-5 h-5 text-[#8D6E63]" />
                  </div>
                  <p className="text-xs text-stone-600">48hr Lead Time</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Shield className="w-5 h-5 text-[#8D6E63]" />
                  </div>
                  <p className="text-xs text-stone-600">Quality Guaranteed</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
